import React, { useState } from 'react';
import { useOrderExtraction } from '../../contexts/OrderExtractionContext';
import inventoryApi from '../../services/api/inventoryApi';
import ExtractedItem from './ExtractedItem';
import './ExtractedItems.css';

const ExtractedItems = ({ items }) => {
  const { actions } = useOrderExtraction();
  const [searchDialog, setSearchDialog] = useState({ 
    isOpen: false, 
    itemIndex: null, 
    searchTerm: '' 
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleQuantityChange = async (itemIndex, newQuantity) => {
    const item = items[itemIndex];
    if (item) {
      try {
        await actions.updateItemQuantity(item.id, item.id, newQuantity);
      } catch (error) {
        alert('Erro ao atualizar quantidade.');
      }
    }
  };

  const handleIgnoreToggle = async (itemIndex, ignored) => {
    const item = items[itemIndex];
    if (item) {
      try {
        await actions.setItemIgnored(item.id, item.id, ignored);
      } catch (error) {
        alert('Erro ao atualizar status do item.');
      }
    }
  };

  const handleCandidateSelect = async (itemIndex, candidate) => {
    const item = items[itemIndex];
    if (item) {
      try {
        await actions.selectProductCode(item.id, item.id, candidate.productCode);
      } catch (error) {
        alert('Erro ao selecionar produto.');
      }
    }
  };

  const handleOpenSearch = (itemIndex, itemName) => {
    setSearchDialog({
      isOpen: true,
      itemIndex: itemIndex,
      searchTerm: itemName
    });
    setSearchResults([]);
  };

  const handleCloseSearch = () => {
    setSearchDialog({ isOpen: false, itemIndex: null, searchTerm: '' });
    setSearchResults([]);
  };

  const handleSearchTermChange = (e) => {
    setSearchDialog(prev => ({
      ...prev,
      searchTerm: e.target.value
    }));
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchProducts();
    }
  };

  const handleSearchProducts = async () => {
    if (!searchDialog.searchTerm.trim()) return;
    
    setIsSearching(true);
    try {
      const results = await inventoryApi.searchProducts(searchDialog.searchTerm);
      
      const transformedResults = results.products.map(product => ({
        Codigo: product.codigoProduto || product.codigo || 'N/A',
        Descricao: product.descricao || product.Descricao || 'Descrição não disponível',
        Unidade: product.unidade || product.Unidade || 'UN',
        Estoque: product.estoque || product.Estoque || 0,
        Minimo: product.minimo || product.Minimo || 0
      }));

      setSearchResults(transformedResults);
    } catch (error) {
      console.error('Error searching products:', error);
      alert('Erro ao pesquisar produtos. Tente novamente.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectFromSearch = (product) => {
    const itemIndex = searchDialog.itemIndex;
    handleCandidateSelect(itemIndex, {
      productCode: product.Codigo,
      productDescription: product.Descricao,
      unityOfMeasure: product.Unidade,
    });
    handleCloseSearch();
  };


  if (!items || items.length === 0) {
    return (
      <div className="extracted-items">
        <div className="extracted-items-header">
          <h3>Itens Extraídos</h3>
        </div>
        <div className="empty-items">
          <div className="empty-icon">📋</div>
          <p>Nenhum item extraído</p>
        </div>
      </div>
    );
  }

  // Separate ignored and non-ignored items
  const nonIgnoredItems = items.filter((item, index) => !item.ignored);
  const ignoredItems = items.filter((item, index) => item.ignored);

  return (
    <div className="extracted-items">
      <div className="extracted-items-header">
        <h3>Itens Extraídos ({items.length})</h3>
        <div className="items-summary">
          <span className="active-count">{nonIgnoredItems.length} ativos</span>
          {ignoredItems.length > 0 && (
            <span className="ignored-count">{ignoredItems.length} ignorados</span>
          )}
        </div>
      </div>

      <div className="items-list">
        {/* Non-ignored items */}
        {nonIgnoredItems.map((item, originalIndex) => {
          const itemIndex = items.findIndex(i => i === item);
          
          return (
            <ExtractedItem
              key={itemIndex}
              item={item}
              itemIndex={itemIndex}
              onQuantityChange={handleQuantityChange}
              onItemIgnored={handleIgnoreToggle}
              onProductSelected={handleCandidateSelect}
              onOpenSearch={handleOpenSearch}
            />
          );
        })}

        {/* Ignored items */}
        {ignoredItems.length > 0 && (
          <div className="ignored-items-section">
            <h4>Itens Ignorados</h4>
            {ignoredItems.map((item, originalIndex) => {
              const itemIndex = items.findIndex(i => i === item);
              return (
                <div key={itemIndex} className="extracted-item ignored">
                  <div className="item-header">
                    <div className="item-info">
                      <span className="item-number">Item {itemIndex + 1}</span>
                      <span className="item-name">{item.productCode}</span>
                      {item.observation && (
                        <span className="item-observation">| {item.observation}</span>
                      )}
                    </div>
                    <div className="item-actions">
                      <button 
                        className="restore-btn"
                        onClick={() => handleIgnoreToggle(itemIndex, false)}
                        title="Restaurar item"
                      >
                        ↩️
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Search Dialog */}
      {searchDialog.isOpen && (
        <div className="search-dialog-overlay">
          <div className="search-dialog">
            <div className="search-dialog-header">
              <h3>Pesquisar Produtos</h3>
              <button 
                className="close-btn"
                onClick={handleCloseSearch}
                title="Fechar"
              >
                ✕
              </button>
            </div>
            
            <div className="search-dialog-content">
              <div className="search-input-section">
                <label htmlFor="search-term">Termo de pesquisa:</label>
                <div className="search-input-group">
                  <input
                    id="search-term"
                    type="text"
                    value={searchDialog.searchTerm}
                    onChange={handleSearchTermChange}
                    onKeyPress={handleSearchKeyPress}
                    placeholder="Digite o nome do produto..."
                    className="search-input"
                  />
                  <button 
                    className="btn btn-primary search-submit-btn"
                    onClick={handleSearchProducts}
                    disabled={isSearching || !searchDialog.searchTerm.trim()}
                  >
                    {isSearching ? 'Pesquisando...' : 'Pesquisar'}
                  </button>
                </div>
              </div>

              {searchResults && searchResults.length > 0 && (
                <div className="search-results">
                  <h4>Resultados da Pesquisa ({searchResults.length})</h4>
                  <div className="search-results-list">
                    {searchResults.map((product, idx) => (
                      <div 
                        key={idx} 
                        className="search-result-item"
                        onClick={() => handleSelectFromSearch(product)}
                      >
                        <div className="search-result-info">
                          <span className="search-result-code">{product.Codigo}</span>
                          <span className="search-result-description">{product.Descricao}</span>
                          <span className="search-result-unit">{product.Unidade}</span>
                        </div>
                        <div className="search-result-stock">
                          Estoque: {product.Estoque} {product.Unidade}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchResults && searchResults.length === 0 && !isSearching && searchDialog.searchTerm && (
                <div className="no-search-results">
                  <p>Nenhum produto encontrado para "{searchDialog.searchTerm}"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtractedItems;
